import {
  Badge,
  Button,
  Card,
  Group,
  Paper,
  Radio,
  SimpleGrid,
  Stack,
  Tabs,
  Text,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useState } from 'react';
import Link from '@/components/Link';
import {
  type MergeCase,
  PERSON_STATE_COLORS,
  PERSON_STATE_LABELS,
  useCases,
  useDecideCase,
} from '@/models/persons';

const CODE_LABELS: Record<string, string> = {
  email_taken: 'Email of another person',
  phone_match: 'Same phone',
  bo_phone_pair: 'Same phone (existing data)',
  bo_link_conflict: 'Monday contact of another person',
  marketing_person_conflict: 'Marketing record differs',
};

function CaseCard({ c }: { c: MergeCase }) {
  const decide = useDecideCase();
  const proposed =
    c.proposal?.keepId != null ? String(c.proposal.keepId) : undefined;
  const [keep, setKeep] = useState<string | undefined>(
    proposed ?? (c.people[0] ? String(c.people[0].id) : undefined),
  );
  const run = async (decision: 'same' | 'different') => {
    const r = await decide.mutateAsync({
      caseId: c.id,
      decision,
      keepId: decision === 'same' && keep ? Number(keep) : null,
    });
    notifications.show({
      color: r.status === 'blocked' ? 'orange' : 'green',
      message:
        r.status === 'blocked'
          ? 'Not merged: the offers of these people lead to different marketing records'
          : decision === 'same'
            ? 'Merged into one person'
            : 'Marked as different people',
    });
  };
  return (
    <Card withBorder>
      <Stack gap="xs">
        <Group justify="space-between">
          <Group gap="xs">
            <Badge variant="light">{CODE_LABELS[c.code] ?? c.code}</Badge>
            <Text size="xs" c="dimmed">
              #{c.id} · {new Date(c.createdAt).toLocaleDateString()}
            </Text>
          </Group>
          {c.comment && (
            <Text size="xs" c="orange.8">
              {c.comment}
            </Text>
          )}
        </Group>
        <Radio.Group value={keep} onChange={setKeep}>
          <SimpleGrid cols={{ base: 1, md: Math.max(2, c.people.length) }}>
            {c.people.map((p) => (
              <Paper key={p.id} withBorder p="xs">
                <Stack gap={2}>
                  <Group gap="xs">
                    <Radio value={String(p.id)} label="Keep" size="xs" />
                    <Link to={`~/contacts/${p.id}`}>
                      {[p.firstName, p.lastName].filter(Boolean).join(' ') ||
                        `#${p.id}`}
                    </Link>
                    {p.state && (
                      <Badge
                        size="xs"
                        color={PERSON_STATE_COLORS[p.state]}
                        variant="light"
                      >
                        {PERSON_STATE_LABELS[p.state]}
                      </Badge>
                    )}
                  </Group>
                  <Text size="xs">
                    {[p.email, p.phone].filter(Boolean).join(' · ')}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {p.requests} requests · {p.offers} offers ·{' '}
                    {p.members.join(', ') || 'no members'}
                    {p.mondayItemId ? ` · Monday ${p.mondayItemId}` : ''}
                  </Text>
                </Stack>
              </Paper>
            ))}
          </SimpleGrid>
        </Radio.Group>
        <Group gap="xs">
          {c.people.length >= 2 && (
            <Button
              size="xs"
              loading={decide.isPending}
              onClick={() => run('same')}
            >
              One person
            </Button>
          )}
          <Button
            size="xs"
            variant="light"
            loading={decide.isPending}
            onClick={() => run('different')}
          >
            {c.people.length >= 2 ? 'Different people' : 'Close'}
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}

export function CasesList({ codes }: { codes: string[] }) {
  const { data, isLoading } = useCases(codes);
  const rows = data?.rows ?? [];
  return (
    <Stack gap="sm">
      {rows.map((c) => (
        <CaseCard key={c.id} c={c} />
      ))}
      {!isLoading && rows.length === 0 && (
        <Text size="sm" c="dimmed">
          No open cases.
        </Text>
      )}
    </Stack>
  );
}

const STEPS: { value: string; label: string; codes: string[] }[] = [
  { value: 'email', label: 'Email', codes: ['email_taken'] },
  { value: 'phone', label: 'Phone', codes: ['phone_match', 'bo_phone_pair'] },
  {
    value: 'links',
    label: 'Links',
    codes: ['bo_link_conflict', 'marketing_person_conflict'],
  },
  { value: 'all', label: 'All', codes: [] },
];

export default function CasesPage() {
  const [step, setStep] = useState('email');
  const codes = STEPS.find((s) => s.value === step)?.codes ?? [];
  return (
    <Paper p="md" h="100%">
      <Stack gap="sm">
        <Tabs value={step} onChange={(v) => setStep(v ?? 'email')}>
          <Tabs.List>
            {STEPS.map((s) => (
              <Tabs.Tab key={s.value} value={s.value}>
                {s.label}
              </Tabs.Tab>
            ))}
          </Tabs.List>
        </Tabs>
        <CasesList codes={codes} />
      </Stack>
    </Paper>
  );
}
